import { useCallback, useEffect, useRef, useState } from 'react';
import type { RoomId } from '@engineer-os/chat';
import { chatPrefs, updatePrefs, type Occupancy } from '../lib/chat';
import { askToNotify, canNotify, fetchInbox, markSeen, notifyMention, roomsWithNews, type InboxMention } from '../lib/inbox';
import { getLocale } from '../lib/locale';

export interface InboxState { mentions: InboxMention[]; elsewhere: number; loading: boolean }
const empty = (): InboxState => ({ mentions: [], elsewhere: 0, loading: false });
interface Context { rooms(): RoomId[]; occupancy(): Record<string, Occupancy> }

/** Account-bound inbox requests and notification lifetime, independent of sockets. */
export function useChatInbox(accountId: string | null, context: Context) {
  const [inbox, setInbox] = useState<InboxState>(empty);
  const [notifyMentions, setNotifyState] = useState(() => chatPrefs().notifyMentions);
  const latest = useRef({ accountId, context, inbox, notifyMentions });
  latest.current = { accountId, context, inbox, notifyMentions };
  const generation = useRef(0);
  const request = useRef<AbortController | null>(null);
  const lastRead = useRef(0);
  const notified = useRef(new Set<number>());
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pullInbox = useCallback(async (force = false) => {
    const userId = latest.current.accountId;
    if (!userId || (!force && Date.now() - lastRead.current < 60_000)) return;
    lastRead.current = Date.now();
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const gen = generation.current;
    setInbox(s => ({ ...s, loading: true }));
    const snap = await fetchInbox(controller.signal);
    if (controller.signal.aborted || request.current !== controller || gen !== generation.current || userId !== latest.current.accountId) return;
    const rooms = latest.current.context.rooms();
    const elsewhere = snap.elsewhere || roomsWithNews(snap.marks, latest.current.context.occupancy(), rooms);
    setInbox({ mentions: snap.mentions, elsewhere, loading: false });
    if (!latest.current.notifyMentions || !canNotify()) return;
    const seen = chatPrefs().seenInbox;
    for (const m of snap.mentions) {
      if (m.id <= seen || notified.current.has(m.id)) continue;
      notified.current.add(m.id);
      if (!rooms.includes(m.room)) notifyMention({ fromName: m.fromName, room: m.room, text: m.text }, getLocale());
    }
  }, []);

  useEffect(() => {
    generation.current += 1;
    lastRead.current = 0;
    notified.current.clear();
    setInbox(empty());
    void pullInbox(true);
    return () => {
      generation.current += 1;
      request.current?.abort();
      if (settleTimer.current !== null) clearTimeout(settleTimer.current);
      settleTimer.current = null;
    };
  }, [accountId, pullInbox]);

  const refreshSoon = useCallback(() => {
    if (settleTimer.current !== null) clearTimeout(settleTimer.current);
    const gen = generation.current;
    settleTimer.current = setTimeout(() => {
      settleTimer.current = null;
      if (gen === generation.current) void pullInbox(true);
    }, 3_000);
  }, [pullInbox]);

  const markInboxSeen = useCallback((ids?: number[]) => {
    if (!latest.current.accountId) return;
    request.current?.abort(); // A GET started before this acknowledgement is stale.
    const gone = ids ? new Set(ids) : null;
    let highest = chatPrefs().seenInbox;
    for (const m of latest.current.inbox.mentions) if (!gone || gone.has(m.id)) highest = Math.max(highest, m.id);
    setInbox(s => ({ ...s, loading: false, mentions: gone ? s.mentions.filter(m => !gone.has(m.id)) : [] }));
    updatePrefs({ seenInbox: highest });
    void markSeen(ids);
  }, []);

  const setNotifyMentions = useCallback(async (on: boolean) => {
    const gen = generation.current;
    const allowed = on ? await askToNotify() : false;
    if (gen !== generation.current) return;
    updatePrefs({ notifyMentions: allowed });
    setNotifyState(allowed);
  }, []);
  return { inbox, pullInbox, refreshSoon, markInboxSeen, notifyMentions, setNotifyMentions, adoptNotificationPreference: setNotifyState };
}
